import React from "react";
import { useNavigate } from "react-router-dom";

// Accurate Rwanda outline as inline SVG — no external image needed
const RwandaMap = ({ opacity = 0.22 }) => (
  <svg
    viewBox="0 0 220 200"
    xmlns="http://www.w3.org/2000/svg"
    style={{ width: "100%", height: "100%" }}
    aria-hidden="true"
  >
    {/* Rwanda border outline — approximated from actual shape */}
    <path
      d="
        M 55 38
        L 68 28
        L 85 24
        L 102 22
        L 118 26
        L 132 32
        L 148 42
        L 158 55
        L 162 68
        L 165 82
        L 162 96
        L 158 110
        L 162 124
        L 158 138
        L 148 150
        L 135 160
        L 118 165
        L 100 168
        L 82 164
        L 66 155
        L 52 142
        L 44 128
        L 40 112
        L 38 96
        L 40 80
        L 44 65
        L 50 52
        Z
      "
      fill="none"
      stroke="#c8a84b"
      strokeWidth="2"
      strokeLinejoin="round"
      opacity={opacity}
    />
    {/* Internal province lines — simplified */}
    <line x1="100" y1="22" x2="100" y2="168" stroke="#c8a84b" strokeWidth="0.8" opacity={opacity * 0.6} strokeDasharray="4 3" />
    <line x1="38" y1="95" x2="165" y2="95" stroke="#c8a84b" strokeWidth="0.8" opacity={opacity * 0.6} strokeDasharray="4 3" />
    {/* Kigali dot */}
    <circle cx="100" cy="95" r="5" fill="#c8a84b" opacity={opacity * 1.8} />
    <circle cx="100" cy="95" r="10" fill="none" stroke="#c8a84b" strokeWidth="1" opacity={opacity} />
    {/* Label */}
    <text
      x="100" y="82"
      textAnchor="middle"
      fill="#c8a84b"
      fontSize="11"
      fontWeight="700"
      fontFamily="sans-serif"
      opacity={opacity * 1.5}
      letterSpacing="2"
    >
      RWANDA
    </text>
  </svg>
);

const HeroCarousel = ({ onScrollDown }) => {
  const navigate = useNavigate();
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100svh",
        minHeight: "560px",
        maxHeight: "100vh",
        overflow: "hidden",
        background: "linear-gradient(135deg, #001233 0%, #003580 60%, #001f4d 100%)",
        touchAction: "pan-y",
      }}
    >
      {/* Rwanda map — right side */}
      <div style={{
        position: "absolute",
        top: "50%",
        right: "clamp(-3rem, 0vw, 3rem)",
        transform: "translateY(-50%)",
        width: "clamp(260px, 44vw, 580px)",
        height: "clamp(260px, 44vw, 580px)",
        zIndex: 1,
        pointerEvents: "none",
      }}>
        <RwandaMap opacity={0.22} />
      </div>

      {/* Dark overlay left-to-right */}
      <div style={{
        position: "absolute", inset: 0, zIndex: 2,
        background: "linear-gradient(to right, rgba(0,18,51,0.95) 0%, rgba(0,18,51,0.72) 50%, rgba(0,18,51,0.2) 100%)",
      }} />

      {/* Bottom fade */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, height: "35%", zIndex: 2,
        background: "linear-gradient(to top, rgba(0,18,51,0.85), transparent)",
      }} />

      {/* Gold top border */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "4px", background: "#c8a84b", zIndex: 5 }} />

      {/* Text content */}
      <div style={{
        position: "absolute", inset: 0, zIndex: 3,
        display: "flex", alignItems: "center",
        padding: "0 clamp(1.5rem, 7vw, 8rem)",
        paddingTop: "clamp(70px, 12vh, 100px)",
        paddingBottom: "clamp(80px, 14vh, 120px)",
      }}>
        <div style={{ maxWidth: "560px" }}>

          {/* Gov badge */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "8px",
            padding: "5px 16px", borderRadius: "4px",
            background: "rgba(200,168,75,0.15)",
            border: "1px solid rgba(200,168,75,0.45)",
            color: "#c8a84b",
            fontSize: "clamp(0.62rem, 1.8vw, 0.72rem)", fontWeight: "700",
            letterSpacing: "0.1em", textTransform: "uppercase",
            marginBottom: "clamp(1rem, 2.5vh, 1.5rem)",
          }}>
            <i className="bi bi-bank2" style={{ fontSize: "0.78rem" }} />
            Repubulika y'u Rwanda
          </div>

          {/* Title */}
          <h1 style={{
            fontSize: "clamp(1.8rem, 6vw, 4rem)",
            fontWeight: "800", color: "#ffffff",
            lineHeight: "1.1",
            marginBottom: "clamp(0.75rem, 2vh, 1.25rem)",
            letterSpacing: "-0.02em",
            textShadow: "0 2px 24px rgba(0,0,0,0.5)",
          }}>
          Inkingi
          </h1>

          {/* Divider */}
          <div style={{ width: "48px", height: "3px", background: "#c8a84b", borderRadius: "2px", marginBottom: "clamp(0.75rem, 2vh, 1.25rem)" }} />

          {/* Subtitle */}
          <p style={{
            fontSize: "clamp(0.88rem, 2.2vw, 1.05rem)",
            color: "rgba(226,232,240,0.80)",
            lineHeight: "1.7",
            marginBottom: "clamp(1.5rem, 3vh, 2.5rem)",
            maxWidth: "440px", fontWeight: "400",
          }}>
            Abayobozi b'inzego z'ibanze bakorana neza n'abaturage mu gutanga serivisi zihuse.
          </p>

          {/* CTA buttons */}
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <button
              onClick={() => navigate("/select-role")}
              style={{
                display: "inline-flex", alignItems: "center", gap: "10px",
                padding: "clamp(12px, 2vh, 15px) clamp(24px, 4vw, 36px)",
                background: "#c8a84b",
                border: "none", borderRadius: "5px",
                color: "#001233", fontWeight: "700",
                fontSize: "clamp(0.88rem, 2vw, 0.95rem)",
                cursor: "pointer",
                boxShadow: "0 4px 20px rgba(200,168,75,0.4)",
                minHeight: "48px",
                touchAction: "manipulation",
                letterSpacing: "0.04em",
              }}
            >
              <i className="bi bi-box-arrow-in-right" />
              Injira
            </button>
          </div>
        </div>
      </div>


    </div>
  );
};

export default HeroCarousel;
